import { useCallback, useEffect, useState } from "react";
import { useLoaderUI } from "../loaderUiContext";
import { SLIDES } from "../slides";
import { SlideBlocks } from "./SlideBlocks";
import { SlideMedia } from "./SlideMedia";

/** Parcel 2.14+ needs this pattern for a real URL string (default .svg import breaks <img> in dev). */
const logoUrl = new URL("../../logo.svg", import.meta.url).href;

export function SlideDeck() {
  const { loading, replayLoader } = useLoaderUI();
  const [index, setIndex] = useState(0);
  const total = SLIDES.length;
  const clamp = useCallback((n) => Math.max(0, Math.min(total - 1, n)), [total]);

  const go = useCallback(
    (dir) => {
      setIndex((i) => clamp(i + dir));
    },
    [clamp]
  );

  useEffect(() => {
    const onKey = (e) => {
      if (loading) return;
      if (e.key === "ArrowRight" || e.key === " " || e.key === "PageDown") {
        e.preventDefault();
        go(1);
      } else if (e.key === "ArrowLeft" || e.key === "PageUp") {
        e.preventDefault();
        go(-1);
      } else if (e.key === "Home") {
        e.preventDefault();
        setIndex(0);
      } else if (e.key === "End") {
        e.preventDefault();
        setIndex(total - 1);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go, loading, total]);

  const slide = SLIDES[index];
  const hasGallery = Boolean(slide.images?.length);

  return (
    <div className="deck">
      <div className="deck__top">
        <button
          type="button"
          className="deck__logo"
          onClick={() => setIndex(0)}
          aria-label="Go to first slide"
        >
          <img src={logoUrl} alt="" width={36} height={36} decoding="async" />
        </button>
        <div className="deck__top-right">
          {!loading ? (
            <button
              type="button"
              className="deck__replay"
              onClick={() => void replayLoader()}
              aria-label="Play loading animation again"
              title="show me the doggo"
            >
              🐕
            </button>
          ) : null}
          <div className="deck__dots" role="tablist" aria-label="Slides">
          {SLIDES.map((s, i) => (
            <button
              key={s.id}
              type="button"
              role="tab"
              aria-selected={i === index}
              className={`deck__dot${i === index ? " deck__dot--on" : ""}`}
              onClick={() => setIndex(i)}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
          </div>
        </div>
      </div>

      <article
        key={slide.id}
        className={`slide${slide.id === "title" ? " slide--hero" : ""}${hasGallery ? " slide--has-gallery" : ""}`}
        id={`slide-${slide.id}`}
        aria-live="polite"
      >
        <div className="slide__text">
          <SlideBlocks blocks={slide.blocks} />
        </div>
        <div className={`slide__below${hasGallery ? "" : " slide__below--empty"}`}>
          <SlideMedia images={slide.images} />
        </div>
      </article>

      <footer className="deck__nav">
        <button type="button" className="btn btn--nav" onClick={() => go(-1)} disabled={index === 0} aria-label="Previous slide">
          ←
        </button>
        <span className="deck__counter">
          {index + 1} / {total}
        </span>
        <button type="button" className="btn btn--nav" onClick={() => go(1)} disabled={index === total - 1} aria-label="Next slide">
          →
        </button>
      </footer>
    </div>
  );
}
